import { FC, ReactNode, useMemo, useCallback } from 'react'
import * as styles from 'components/features/socialFeed/FeedUserContextInfo/FeedUserContextInfo.styl'
import * as cn from 'classnames'
import { useStoreDispatch } from 'helpers/reactReduxHelpers'
import { NavigationLink } from 'components/atoms/navigation/NavigationLink'
import { FeedUserUid, MatchingPeergroupFilled } from 'interfaces/feed'
import { PeerDefinition } from 'components/features/pfm/PeerDefinition/PeerDefinition'
import { PeergroupDefinition } from 'interfaces/peergroups'
import { preparePeerDefinitionProps } from 'helpers/peergroupsHelpers'
import SvgIcon from 'components/atoms/SvgIcon'
import CheckmarkSvg from 'icons/checkmark.svg'
import { FeedUserBadgesExtended } from 'components/features/socialFeed/FeedUserInfo/FeedUserBadgesExtended'
import { UserAvatar } from 'components/features/socialFeed/Avatar/UserAvatar'
import { FeedUserCounters } from 'components/features/socialFeed/FeedUserCounters/FeedUserCounters'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { createFeedUserSelector } from 'selectors/feedSelectors'
import { memberSelector } from 'selectors/globalSelectors'
import { ResourceFollowButton } from 'components/features/pfm/ResourceFollowButton/ResourceFollowButton'
import { viewportSegmentSelector } from 'selectors/viewportSelectors'
import { showProfileEditFormActionCreator } from 'actions/settingsActions'
import TextButton from 'components/atoms/buttons/TextButton'

type FeedUserContextInfoProps = {
  userUid: FeedUserUid
  peergroup: MatchingPeergroupFilled
  username: string
  disclaimer?: string
}

type RenderPropsFunc = (value: ReactNode, key: keyof PeergroupDefinition) => ReactNode

export const FeedUserContextInfo: FC<FeedUserContextInfoProps> = (props) => {
  const {
    peergroup,
    userUid,
    username,
    disclaimer,
  } = props

  const { parameters, match } = peergroup
  const feedUserSelector = useMemo(createFeedUserSelector, [])
  const user = useStoreSelector((state) => feedUserSelector(state, props))
  const member = useStoreSelector(memberSelector)
  const viewportSegment = useStoreSelector(viewportSegmentSelector)

  const followButtonMode = useMemo(() => {
    if (viewportSegment === 'palm-mobile') {
      return 'large'
    }

    return 'medium'
  }, [viewportSegment])

  const userIsSelf = Boolean(member.data && member.data.info && user && member.data.info.uid === user.uid)

  const renderProp = useMemo<RenderPropsFunc>(() => {
    const matchingParametersSet = new Set(match.parameters)

    return (value, key) => {
      const isMatched = matchingParametersSet.has(key)

      const checkmark = (
        <div className={ styles.checkmarkContainer }>
          { !isMatched && <div className={ styles.checkmarkEmpty } /> }
          { isMatched && <SvgIcon src={ CheckmarkSvg } useDefaultViewBox /> }
        </div>
      )

      return (
        <div className={ cn(styles.definitionValue, (isMatched && !userIsSelf) && styles.isMatched) }>
          { !userIsSelf && checkmark }
          { value }
        </div>
      )
    }
  }, [match.parameters, userIsSelf])

  const feedUserCountersChild = useMemo(() => {
    if (!user) {
      return null
    }

    const { likes_count, followers_count, posts_count } = user

    return (
      <div className={ styles.counters }>
        <FeedUserCounters
          likesCount={ likes_count }
          followersCount={ followers_count }
          postsCount={ posts_count }
          mode='compact'
        />
      </div>
    )
  }, [user])


  const dispatch = useStoreDispatch()

  const handleLinkClick = useCallback(() => {
    dispatch(showProfileEditFormActionCreator(true))
  }, [dispatch])

  const callToAction = !userIsSelf ? (
    <div className={ styles.followButtonWrapper }>
      <ResourceFollowButton
        mode={ followButtonMode }
        type='user'
        name={ username }
      />
    </div>
  ) : (
    <NavigationLink to='settings' className={ styles.textCta }>
      <TextButton size='regular' onClick={ handleLinkClick }>Update your picture</TextButton>
    </NavigationLink>
  )

  const matchText = Math.round(match.ratio * 100) + '% your match'

  const disclaimerComponent = disclaimer && (
    <span className={ styles.disclaimer }>{ disclaimer }</span>
  )

  return (
    <div className={ styles.feedUserContextInfo }>
      <div className={ cn(styles.innerWrapper, { [styles.you]: userIsSelf }) }>
        <span className={ styles.matchPercent }>
          { userIsSelf ? 'You' : matchText }
        </span>
        <div className={ styles.avatar }>
          <UserAvatar
            userUid={ userUid }
            matchRatio={ match.ratio }
          />
        </div>
        <NavigationLink
          to={{ anchor: 'feedauthor', id: username }}
          className={ styles.username }
        >
          { username }
        </NavigationLink>

        { feedUserCountersChild }

        { user && <FeedUserBadgesExtended user={ user } /> }

        { callToAction }

        <hr className={ styles.hr } />
        { disclaimerComponent }
        <div className={ styles.definitions }>
          <PeerDefinition
            isRange
            { ...preparePeerDefinitionProps(parameters, renderProp) }
          />
        </div>
      </div>
    </div>
  )
}
